import "./style.scss";

import ManageSearchOutlinedIcon from "@mui/icons-material/ManageSearchOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import SyncIcon from "@mui/icons-material/Sync";
import { Collapse, InputAdornment, Skeleton } from "@mui/material";
import Google from "assets/img/svg/googleIcon";
import type { INewStatementForm } from "components/PartnerStatementsSettings/types";
import DocumentationLink from "components/shared/DocumentationLink";
import EmptyPopState from "components/shared/EmptyPopState";
import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import TextFormComponent from "components/shared/FormComponent/TextFormComponent";
import StyledButton from "components/shared/StyledButton";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { getIntegrationAuthorName } from "helpers/integrationHelpers";
import { formatAttributeName, formatLocaleDateString } from "helpers/utils";
import { useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { IIntegrations } from "store/integrationsStore";
import { useAppSelector } from "store/store";

import ConnectIntegrationModal from "./ConnectIntegrationModal";
import DynamicStatus from "./DynamicStatus";
import SearchSheetBlock from "./SearchSheetBlock";
import SearchSheetModal from "./SearchSheetModal";
import SpreadSheetRow from "./SpreadSheetRow";

/**
 * This component is the integration/sync part from the new statement form
 */
interface IStatementIntegration {
    stepTitle: string;
    stepSubtitle: string;
    indexStep: number;
    /**
     * Active company integrations
     */
    activeIntegrations: IIntegrations[];
    mode: "edit" | "new";
    /**
     * Search for another Google Sheets template, on edit mode
     */
    onValidateNewTemplate: () => void;
}

const StatementIntegration = ({
    stepTitle,
    stepSubtitle,
    indexStep,
    activeIntegrations,
    mode,
    onValidateNewTemplate,
}: IStatementIntegration) => {
    const [t] = useTranslation();
    const {
        control,
        formState: { errors },
        watch,
    } = useFormContext<INewStatementForm>();

    const users = useAppSelector((state) => state.adminPanel.users.company.data);
    const { googleSpreadSheets, fetchGoogleSpreadsheetStatus, selectedSheet } = useAppSelector(
        (state) => state.partnerStatements,
    );
    const [openConnectModal, setOpenConnectModal] = useState(false);
    const [openSearchSheetModal, setOpenSearchSheetModal] = useState(false);

    const isSelectedSheet = selectedSheet.id != null;
    const integration = watch("integration");
    const connectedUserName = integration ? getIntegrationAuthorName(integration, users, t) : "";

    const integrationIcon = {
        google: <Google />,
    };

    const renderInputIntegration = (option: any) => {
        const integrationName = option.inputProps.value.toLowerCase();
        return (
            <StyledTextField
                {...option}
                className="new-integration-autocomplete"
                placeholder={t("PartnerStatements.ChooseIntegration")}
                InputProps={{
                    ...option.InputProps,
                    startAdornment: (
                        <InputAdornment position="end" className="new-integration-adornment">
                            <div className="new-integration-adornment-icon">
                                {integrationName ? integrationIcon[integrationName] : <SyncIcon />}
                            </div>
                        </InputAdornment>
                    ),
                    endAdornment: (
                        <div className="new-integration-adornment-end">
                            {connectedUserName}
                            {option.InputProps.endAdornment}
                        </div>
                    ),
                }}
            />
        );
    };

    const renderIntegrationsOptions = (props: any, option: any) => {
        return (
            <li {...props} key={`integration-option-${option.id}`} className="new-integration-options-list">
                <div className="integration-option-left">
                    {integrationIcon[option.partner]}
                    <div className="integration-option-name">
                        <span>{formatAttributeName(option.partner)}</span>
                        <span>
                            {`${t(
                                option.accountEmail
                                    ? "PartnerStatements.GoogleAccount"
                                    : "PartnerStatements.ConnectedBy",
                            )} ${getIntegrationAuthorName(option, users, t)}`}
                        </span>
                    </div>
                </div>
                <div className="integration-option-right">
                    {`${t("IntegrationsSettings.Added")} ${formatLocaleDateString({
                        date: option?.created,
                        options: { numeric: true, timeZone: "UTC" },
                    })}`}
                </div>
            </li>
        );
    };

    const onOpenSearchSheetModal = () => {
        setOpenSearchSheetModal(true);
    };

    return (
        <div className="new-statement-integrations">
            <ConnectIntegrationModal open={openConnectModal} onClose={() => setOpenConnectModal(false)} />
            <SearchSheetModal
                mode={mode}
                open={openSearchSheetModal}
                onClose={() => setOpenSearchSheetModal(false)}
                currentIntegration={integration as IIntegrations}
                onValidateNewTemplate={onValidateNewTemplate}
            />
            <h3 className="form-section-title">
                {indexStep + 1}. {stepTitle}
            </h3>
            <span className="form-section-description">{stepSubtitle}</span>
            <div className="form-fields">
                {activeIntegrations.length > 0 && (
                    <div className="new-statement-choose-integration">
                        <AutocompleteFormComponent
                            controlName="integration"
                            control={control}
                            label={mode === "new" ? t("PartnerStatements.ChooseIntegration") : "Integration"}
                            placeholder={t("PartnerStatements.ChooseIntegration")}
                            renderInput={renderInputIntegration}
                            renderOption={renderIntegrationsOptions}
                            renderOptionText={(option) => (option?.partner ? formatAttributeName(option.partner) : "")}
                            options={activeIntegrations}
                            required
                            error={errors.integration}
                            loading={true}
                        />
                        {!!activeIntegrations.length && (
                            <DynamicStatus
                                data={integration}
                                defaultText={t("PartnerStatements.NoSelection")}
                                activeText={integration ? `Google Sheets <> ${connectedUserName}` : ""}
                                pending={fetchGoogleSpreadsheetStatus === "pending"}
                                succeeded={
                                    mode === "new" ? fetchGoogleSpreadsheetStatus === "succeeded" : !!integration
                                }
                                renderStatusRight={
                                    <span>
                                        {!!googleSpreadSheets.length &&
                                            `${googleSpreadSheets.length} ${t("PartnerStatements.SpreadSheets")}`}
                                    </span>
                                }
                            />
                        )}
                    </div>
                )}
                {mode === "edit" && (
                    <>
                        <TextFormComponent
                            control={control}
                            controlName="name"
                            label={t("PartnerStatements.TemplateName")}
                            placeholder={t("PartnerStatements.TemplateName")}
                            required
                            error={errors.name}
                        />
                        <Collapse in={!!integration} timeout={"auto"}>
                            <div className="search-another-spreadsheet">
                                <StyledButton
                                    buttonType="outlined"
                                    sx={{ width: "max-content" }}
                                    onClick={onOpenSearchSheetModal}
                                >
                                    <ManageSearchOutlinedIcon sx={{ width: "20px", height: "20px" }} />
                                    {t("PartnerStatements.SearchAnotherTemplate")}
                                </StyledButton>
                                <span>
                                    {connectedUserName &&
                                        `${t("PartnerStatements.SearchAnotherSpreadsheet")} ${connectedUserName}`}
                                </span>
                            </div>
                        </Collapse>
                    </>
                )}
                {!activeIntegrations.length && (
                    <EmptyPopState
                        title={t("PartnerStatements.ConfigureIntegration")}
                        description={t("PartnerStatements.AssociateIntegrationInfo")}
                        btnTxt={t("PartnerStatements.AddIntegration")}
                        onSubmit={() => {
                            setOpenConnectModal(true);
                        }}
                    />
                )}
                {mode === "new" && <SearchSheetBlock currentIntegration={integration as IIntegrations} mode={mode} />}
                {!!googleSpreadSheets.length && !isSelectedSheet && mode === "new" && (
                    <span className="new-statement-spreadsheets-info">{t("PartnerStatements.SelectASheet")}</span>
                )}
            </div>
        </div>
    );
};

export default StatementIntegration;
